import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  CREATE_SESSION_VALUE,
  RENAME_SESSION_VALUE,
  useForecastSessionPicker,
} from 'src/features/forecastSessions/useForecastSessionPicker';
import { cn } from 'src/lib/utils';

type ForecastSessionPickerProps = {
  className?: string;
};

export function ForecastSessionPicker({
  className,
}: ForecastSessionPickerProps): ReactElement {
  const { t } = useTranslation('forecast');
  const {
    sessions,
    activeSession,
    isBusy,
    isCreating,
    isRenaming,
    isSelectOpen,
    isCreateDialogOpen,
    isRenameDialogOpen,
    createSessionName,
    renameSessionName,
    setCreateSessionName,
    setRenameSessionName,
    setIsCreateDialogOpen,
    setIsRenameDialogOpen,
    closeCreateDialog,
    closeRenameDialog,
    handleSelectOpenChange,
    handleSessionChange,
    handleCreateSession,
    handleRenameSession,
  } = useForecastSessionPicker();

  return (
    <>
      <div className={cn('flex items-center gap-3', className)}>
        <Select
          value={activeSession?.id ?? undefined}
          open={isSelectOpen}
          onOpenChange={handleSelectOpenChange}
          onValueChange={handleSessionChange}
          disabled={isBusy}
        >
          <SelectTrigger
            id='forecast-session-select'
            className='w-[17rem]'
            disabled={isBusy}
          >
            <SelectValue placeholder={t('Select a session')} />
          </SelectTrigger>
          <SelectContent>
            {sessions.map((session) => (
              <SelectItem key={session.id} value={session.id}>
                {session.name}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={CREATE_SESSION_VALUE}>
              {t('Create new session')}
            </SelectItem>
            <SelectItem
              value={RENAME_SESSION_VALUE}
              disabled={activeSession === undefined}
            >
              {t('Rename current session')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            closeCreateDialog();
          }
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('Create new session')}</DialogTitle>
          </DialogHeader>
          <div className='grid gap-2'>
            <span className='text-sm font-medium'>{t('Session name')}</span>
            <Input
              id='forecast-session-name'
              value={createSessionName}
              onChange={(event) => setCreateSessionName(event.target.value)}
              placeholder={t('Session name')}
              maxLength={255}
              autoFocus
            />
          </div>
          <DialogFooter className='gap-2'>
            <Button
              variant='outline'
              onClick={closeCreateDialog}
              disabled={isCreating}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleCreateSession}
              disabled={isCreating || !createSessionName.trim()}
            >
              {t('Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isRenameDialogOpen}
        onOpenChange={(open) => {
          setIsRenameDialogOpen(open);
          if (!open) {
            closeRenameDialog();
          }
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('Rename session')}</DialogTitle>
          </DialogHeader>
          <div className='grid gap-2'>
            <span className='text-sm font-medium'>{t('Session name')}</span>
            <Input
              id='forecast-session-rename'
              value={renameSessionName}
              onChange={(event) => setRenameSessionName(event.target.value)}
              placeholder={t('Session name')}
              maxLength={255}
              autoFocus
            />
          </div>
          <DialogFooter className='gap-2'>
            <Button
              variant='outline'
              onClick={closeRenameDialog}
              disabled={isRenaming}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleRenameSession}
              disabled={isRenaming || !renameSessionName.trim()}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
